import type { InteractionState } from '../session.js';

/**
 * Human-readable CLI (Caller Line Identification) match status for the
 * persistent agent banner (CCF-2 / CCF-3 / CCF-12).
 *
 * The banner's "CLI match" field tells the agent whether the live caller's
 * number (CLI/ANI) matches the mobile number PTSB holds on file. That is only a
 * meaningful comparison **during a live customer call** — i.e. when the
 * interaction is **Online**. The display rule is therefore driven by the
 * interaction state, not just the raw gateway flag:
 *
 *   | Interaction state | CLI vs registered mobile | Display |
 *   |-------------------|--------------------------|---------|
 *   | Offline           | (n/a — no live call)     | N/A     |
 *   | Online            | matches                  | Yes     |
 *   | Online            | does not match           | N       |
 *
 * Offline → "N/A" always: with no live interaction there is no caller CLI to
 * compare, so the field must never assert a match (or a non-match).
 *
 * Online → the gateway-supplied match flag is normalised to a positive/negative
 * answer. The Bank gateway returns a `Y`/`N` flag (it never returns the
 * unmasked registered number); any positive marker (`Y`, `Yes`, `true`,
 * `match`) renders as **"Yes"**, anything else (including a missing/undefined
 * value — e.g. when the call carried no CLI) renders as **"N"**.
 *
 * Single source of truth for the CLI-match label — reuse this wherever the
 * field is shown rather than echoing the raw `cliMatch` string.
 */
export function cliMatchLabel(
  state: InteractionState,
  cliMatch: string | undefined,
): string {
  // Offline: no live call, so the comparison does not apply.
  if (state !== 'online') return 'N/A';

  const normalised = String(cliMatch ?? '')
    .trim()
    .toLowerCase();
  const isMatch =
    normalised === 'y' ||
    normalised === 'yes' ||
    normalised === 'true' ||
    normalised === 'match';
  return isMatch ? 'Yes' : 'N';
}
